import React, { useEffect, useRef, useState } from 'react';
import { api, ApiError, downloadFile, mutate, uploadFile } from '../shared/api';
import { formatMoney, formatNumber, formatDate, formatMoneyInput, parseMoneyInput } from '../shared/formatting';

type Locale = 'hr' | 'en';
type Section = 'arhiva' | 'nacrti';
type Radna = 'zajednicka' | 'moja';

const num: React.CSSProperties = { textAlign: 'right', whiteSpace: 'nowrap' };
const KONTO_LIST_ID = 'gl-temeljnice-konta-list';

function qs(params: Record<string, string | number | boolean | undefined>): string {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === '' || v === false) return;
        q.set(k, v === true ? '1' : String(v));
    });
    return q.toString();
}

// ---------------------------------------------------------------------------
// Arhiva temeljnica
// ---------------------------------------------------------------------------

type VrstaOpt = { vrsta: string; naziv: string; n: number };
type TemeljnicaRow = {
    vrsta: string; broj: string; opis: string | null; vrsta_naziv: string | null; datum: string | null;
    knj_od: string | null; knj_do: string | null; n: number; konta: number; duguje: number; potrazuje: number;
    razlika: number; uravnotezena: boolean; promjena: 'nova' | 'promijenjena' | null;
};
type ArhivaResp = { total: number; page: number; page_size: number; rows: TemeljnicaRow[]; vrste: VrstaOpt[] };
type DetailLine = {
    id: number; konto: string; naziv_konta: string | null; partner: string | null; dokument: string | null;
    datum_knjizenja: string | null; opis: string | null; mtp: string | null; duguje: number; potrazuje: number;
};
type DetailResp = { rows: DetailLine[] };

type ArhivaFilters = { vrsta: string; broj: string; konto_od: string; konto_do: string; neuravnotezene: boolean };
const EMPTY_ARHIVA_FILTERS: ArhivaFilters = { vrsta: '', broj: '', konto_od: '', konto_do: '', neuravnotezene: false };

function ChangeBadge({ promjena, locale }: { promjena: TemeljnicaRow['promjena']; locale: Locale }) {
    if (!promjena) return null;
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const label = promjena === 'nova' ? t('nova', 'new') : t('promijenjena', 'changed');
    const color = promjena === 'nova' ? '#1f7a36' : '#b45309';
    return <span style={{ fontSize: '0.72rem', fontWeight: 700, color, background: `${color}18`, padding: '1px 6px', borderRadius: 6 }}>{label}</span>;
}

function ArhivaSection({ locale, token, company, godina }: { locale: Locale; token: string | null; company: string; godina: number }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [draft, setDraft] = useState<ArhivaFilters>(EMPTY_ARHIVA_FILTERS);
    const [applied, setApplied] = useState<ArhivaFilters>(EMPTY_ARHIVA_FILTERS);
    const [page, setPage] = useState(1);
    const [data, setData] = useState<ArhivaResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState<string | null>(null);
    const [detail, setDetail] = useState<Record<string, DetailLine[]>>({});
    const [detailLoading, setDetailLoading] = useState<string | null>(null);
    const [detailError, setDetailError] = useState<string | null>(null);

    useEffect(() => {
        setDraft(EMPTY_ARHIVA_FILTERS);
        setApplied(EMPTY_ARHIVA_FILTERS);
        setPage(1);
        setOpen(null);
        setDetail({});
    }, [company, godina]);

    useEffect(() => {
        setLoading(true);
        setError(null);
        const query = qs({
            company, godina, vrsta: applied.vrsta, broj: applied.broj,
            konto_od: applied.konto_od, konto_do: applied.konto_do, neuravnotezene: applied.neuravnotezene, page,
        });
        api<ArhivaResp>(`/gl-analitika/temeljnice?${query}`, token)
            .then(setData)
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')))
            .finally(() => setLoading(false));
    }, [applied, page, company, godina, token]);

    const apply = () => { setPage(1); setApplied({ ...draft }); };

    const toggle = (row: TemeljnicaRow) => {
        const key = `${row.vrsta}|${row.broj}`;
        if (open === key) { setOpen(null); return; }
        setOpen(key);
        if (!detail[key]) {
            setDetailLoading(key);
            setDetailError(null);
            api<DetailResp>(`/gl-analitika/temeljnica?${qs({ company, godina, vrsta: row.vrsta, broj: row.broj })}`, token)
                .then((r) => setDetail((d) => ({ ...d, [key]: r.rows })))
                .catch((e) => setDetailError(e instanceof ApiError ? e.message : t('Učitavanje stavki nije uspjelo.', 'Loading lines failed.')))
                .finally(() => setDetailLoading(null));
        }
    };

    const m = (v: number) => formatMoney(v, locale);
    const pages = data ? Math.max(1, Math.ceil(data.total / (data.page_size || 50))) : 1;

    return (
        <div>
            <form onSubmit={(e) => { e.preventDefault(); apply(); }} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.8rem' }}>
                <label>{t('Vrsta', 'Type')}<br />
                    <select value={draft.vrsta} onChange={(e) => setDraft((d) => ({ ...d, vrsta: e.target.value }))}>
                        <option value="">{t('sve', 'all')}</option>
                        {(data?.vrste ?? []).map((v) => <option key={v.vrsta} value={v.vrsta}>{v.vrsta} · {v.naziv} ({v.n})</option>)}
                    </select>
                </label>
                <label>{t('Broj', 'Number')}<br /><input value={draft.broj} onChange={(e) => setDraft((d) => ({ ...d, broj: e.target.value }))} style={{ width: 110 }} /></label>
                <label>{t('Konto od', 'Account from')}<br /><input value={draft.konto_od} onChange={(e) => setDraft((d) => ({ ...d, konto_od: e.target.value }))} style={{ width: 90 }} /></label>
                <label>{t('Konto do', 'Account to')}<br /><input value={draft.konto_do} onChange={(e) => setDraft((d) => ({ ...d, konto_do: e.target.value }))} style={{ width: 90 }} /></label>
                <label><input type="checkbox" checked={draft.neuravnotezene} onChange={(e) => setDraft((d) => ({ ...d, neuravnotezene: e.target.checked }))} /> {t('samo neuravnotežene', 'only unbalanced')}</label>
                <button type="submit" className="compact">{t('Prikaži', 'Show')}</button>
                {loading ? <span className="muted">{t('Učitavam…', 'Loading…')}</span> : null}
            </form>

            {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : null}

            {data ? (
                <>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '0.4rem 0' }}>
                        <span className="muted">{formatNumber(data.total, locale)} {t('temeljnica', 'journals')} · {t('stranica', 'page')} {data.page} / {pages}</span>
                        <button type="button" className="secondary compact" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>‹</button>
                        <button type="button" className="secondary compact" disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)}>›</button>
                    </div>
                    <div style={{ overflow: 'auto' }}>
                        <table className="data-table compact">
                            <thead><tr>
                                <th>{t('Vrsta', 'Type')}</th><th>{t('Broj', 'Number')}</th><th>{t('Datum (razdoblja)', 'Date (period)')}</th>
                                <th>{t('Opis', 'Description')}</th><th>{t('Vrsta dok.', 'Doc type')}</th><th style={num}>{t('Stavki', 'Lines')}</th>
                                <th style={num}>{t('Duguje', 'Debit')}</th><th style={num}>{t('Potražuje', 'Credit')}</th><th style={num}>{t('Razlika', 'Difference')}</th>
                                <th>{t('Promjena', 'Change')}</th>
                            </tr></thead>
                            <tbody>
                                {data.rows.map((r) => {
                                    const key = `${r.vrsta}|${r.broj}`;
                                    const isOpen = open === key;
                                    const range = r.knj_od && r.knj_do ? `${formatDate(r.knj_od, locale)} - ${formatDate(r.knj_do, locale)}` : '';
                                    return (
                                        <React.Fragment key={key}>
                                            <tr style={{ cursor: 'pointer' }} onClick={() => toggle(r)}>
                                                <td>{isOpen ? '▾' : '▸'} {r.vrsta}</td><td>{r.broj}</td>
                                                <td title={range}>{r.datum ? formatDate(r.datum, locale) : ''}</td>
                                                <td>{r.opis ?? ''}</td><td>{r.vrsta_naziv ?? ''}</td><td style={num}>{formatNumber(r.n, locale)}</td>
                                                <td style={num}>{m(r.duguje)}</td><td style={num}>{m(r.potrazuje)}</td>
                                                <td style={{ ...num, fontWeight: r.uravnotezena ? 400 : 700, color: r.uravnotezena ? undefined : '#b91c1c' }}>{m(r.razlika)}</td>
                                                <td><ChangeBadge promjena={r.promjena} locale={locale} /></td>
                                            </tr>
                                            {isOpen ? (
                                                <tr>
                                                    <td colSpan={10} style={{ background: 'rgba(0,0,0,0.02)' }}>
                                                        {detailLoading === key ? <span className="muted">{t('Učitavam stavke…', 'Loading lines…')}</span>
                                                            : detailError ? <p style={{ color: '#b91c1c' }}>{detailError}</p>
                                                                : (
                                                                    <table className="data-table compact" style={{ margin: '4px 0' }}>
                                                                        <thead><tr>
                                                                            <th>{t('Konto', 'Account')}</th><th>{t('Partner', 'Partner')}</th><th>{t('Dokument', 'Document')}</th>
                                                                            <th>{t('Datum knjiženja', 'Posting date')}</th><th>{t('Opis', 'Description')}</th><th>MTP</th>
                                                                            <th style={num}>{t('Duguje', 'Debit')}</th><th style={num}>{t('Potražuje', 'Credit')}</th>
                                                                        </tr></thead>
                                                                        <tbody>
                                                                            {(detail[key] ?? []).map((l) => (
                                                                                <tr key={l.id}>
                                                                                    <td title={l.naziv_konta ?? ''}>{l.konto}</td><td>{l.partner ?? ''}</td><td>{l.dokument ?? ''}</td>
                                                                                    <td>{l.datum_knjizenja ? formatDate(l.datum_knjizenja, locale) : ''}</td><td>{l.opis ?? ''}</td><td>{l.mtp ?? ''}</td>
                                                                                    <td style={num}>{l.duguje ? m(l.duguje) : ''}</td><td style={num}>{l.potrazuje ? m(l.potrazuje) : ''}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                )}
                                                    </td>
                                                </tr>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : null}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Nacrti temeljnica
// ---------------------------------------------------------------------------

type NacrtStatus = 'nacrt' | 'odobreno' | 'proknjizeno' | 'odbaceno';
type NacrtHead = {
    id: number; radna: Radna; godina: number; vrsta: string | null; datum: string | null; opis: string; status: NacrtStatus;
    izvor: string | null; izvor_opis: string | null; stavki: number; duguje: number; potrazuje: number; uravnotezen: boolean;
    faros_vrsta: string | null; faros_broj: string | null; napomena: string | null; autor: string | null;
    status_at: string | null; created_at: string | null;
};
type NacrtiResp = { nacrti: NacrtHead[]; prijelazi: Partial<Record<NacrtStatus, NacrtStatus[]>> };
type KontoOpt = { konto: string; naziv: string | null };
type Stavka = {
    rb?: number; konto: string; partner_sifra: string | null; partner: string | null; mtp: string | null;
    dokument: string | null; opis: string | null; datum_knjizenja: string | null; duguje: number; potrazuje: number;
};
type NacrtDetailResp = { nacrt: NacrtHead; stavke: Stavka[] };

const STATUS_LABEL: Record<NacrtStatus, [string, string]> = {
    nacrt: ['Nacrt', 'Draft'],
    odobreno: ['Odobreno (za knjiženje)', 'Approved (to post)'],
    proknjizeno: ['Proknjiženo u Farosu', 'Posted in Faros'],
    odbaceno: ['Odbačeno', 'Rejected'],
};
const STATUS_COLOR: Record<NacrtStatus, string> = { nacrt: '#555', odobreno: '#b45309', proknjizeno: '#1f7a36', odbaceno: '#b91c1c' };

type RowState = {
    _key: string; konto: string; partner_sifra: string; partner: string; mtp: string; dokument: string;
    opis: string; datum_knjizenja: string; duguje: string; potrazuje: string;
};

let rowSeq = 0;
function newRow(): RowState {
    rowSeq += 1;
    return { _key: `r${rowSeq}`, konto: '', partner_sifra: '', partner: '', mtp: '', dokument: '', opis: '', datum_knjizenja: '', duguje: '', potrazuje: '' };
}
function rowFromStavka(s: Stavka, locale: Locale): RowState {
    rowSeq += 1;
    return {
        _key: `r${rowSeq}`, konto: s.konto ?? '', partner_sifra: s.partner_sifra ?? '', partner: s.partner ?? '',
        mtp: s.mtp ?? '', dokument: s.dokument ?? '', opis: s.opis ?? '', datum_knjizenja: s.datum_knjizenja ? s.datum_knjizenja.slice(0, 10) : '',
        duguje: s.duguje ? formatMoneyInput(s.duguje, locale) : '', potrazuje: s.potrazuje ? formatMoneyInput(s.potrazuje, locale) : '',
    };
}

function NacrtEditor({ locale, token, company, godina, id, radna, onClose, onSaved }: {
    locale: Locale; token: string | null; company: string; godina: number; id: number | 'new'; radna: Radna; onClose: () => void; onSaved: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [loading, setLoading] = useState(id !== 'new');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<NacrtStatus>('nacrt');
    const [headDatum, setHeadDatum] = useState(new Date().toISOString().slice(0, 10));
    const [headVrsta, setHeadVrsta] = useState('');
    const [headOpis, setHeadOpis] = useState('');
    const [headNapomena, setHeadNapomena] = useState('');
    const [rows, setRows] = useState<RowState[]>(() => (id === 'new' ? [newRow()] : []));
    const [truncatedTotal, setTruncatedTotal] = useState<number | null>(null);
    const [serverTotals, setServerTotals] = useState<{ d: number; p: number } | null>(null);
    const [kontoOptions, setKontoOptions] = useState<KontoOpt[]>([]);
    const [kontoCache, setKontoCache] = useState<Record<string, string>>({});
    const kontoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (id === 'new') return;
        setLoading(true);
        setError(null);
        api<NacrtDetailResp>(`/gl-analitika/nacrti/${id}?${qs({ company })}`, token)
            .then((r) => {
                setStatus(r.nacrt.status);
                setHeadDatum(r.nacrt.datum ? r.nacrt.datum.slice(0, 10) : '');
                setHeadVrsta(r.nacrt.vrsta ?? '');
                setHeadOpis(r.nacrt.opis ?? '');
                setHeadNapomena(r.nacrt.napomena ?? '');
                const limited = r.stavke.length > 500 ? r.stavke.slice(0, 500) : r.stavke;
                setRows(limited.map((s) => rowFromStavka(s, locale)));
                if (r.nacrt.stavki > 500) {
                    setTruncatedTotal(r.nacrt.stavki);
                    setServerTotals({ d: r.nacrt.duguje, p: r.nacrt.potrazuje });
                } else {
                    setTruncatedTotal(null);
                    setServerTotals(null);
                }
            })
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')))
            .finally(() => setLoading(false));
    }, [id, company, token]);

    const canEditHeader = id === 'new' || status === 'nacrt';
    const canEditStavke = canEditHeader && truncatedTotal === null;

    const updateRow = <K extends keyof RowState>(key: string, field: K, value: RowState[K]) => {
        setRows((rs) => rs.map((r) => (r._key === key ? { ...r, [field]: value } : r)));
    };
    const addRow = () => setRows((rs) => [...rs, newRow()]);
    const removeRow = (key: string) => setRows((rs) => rs.filter((r) => r._key !== key));

    const fetchKonta = (q: string) => {
        if (kontoTimer.current) clearTimeout(kontoTimer.current);
        kontoTimer.current = setTimeout(() => {
            api<{ konta: KontoOpt[] }>(`/gl-analitika/konta?${qs({ company, q })}`, token)
                .then((r) => {
                    setKontoOptions(r.konta);
                    setKontoCache((c) => {
                        const next = { ...c };
                        r.konta.forEach((k) => { next[k.konto] = k.naziv ?? ''; });
                        return next;
                    });
                })
                .catch(() => { /* autocomplete best-effort, ignore errors */ });
        }, 250);
    };

    const sums = serverTotals ?? rows.reduce((acc, r) => {
        acc.d += parseMoneyInput(r.duguje) ?? 0;
        acc.p += parseMoneyInput(r.potrazuje) ?? 0;
        return acc;
    }, { d: 0, p: 0 });
    const razlika = sums.d - sums.p;

    async function save() {
        if (!headOpis.trim()) { setError(t('Opis je obavezan.', 'Description is required.')); return; }
        setSaving(true);
        setError(null);
        try {
            const body: Record<string, unknown> = {
                godina, datum: headDatum || null, vrsta: headVrsta || null, opis: headOpis.trim(), napomena: headNapomena || null,
            };
            if (canEditStavke) {
                body.stavke = rows.map((r, i) => ({
                    rb: i + 1, konto: r.konto, partner_sifra: r.partner_sifra || null, partner: r.partner || null,
                    mtp: r.mtp || null, dokument: r.dokument || null, opis: r.opis || null,
                    datum_knjizenja: r.datum_knjizenja || null, duguje: parseMoneyInput(r.duguje) ?? 0, potrazuje: parseMoneyInput(r.potrazuje) ?? 0,
                }));
            }
            if (id === 'new') {
                await mutate<{ id: number }>(`/gl-analitika/nacrti?${qs({ company })}`, token, 'POST', { ...body, radna });
            } else {
                await mutate(`/gl-analitika/nacrti/${id}?${qs({ company })}`, token, 'PUT', body);
            }
            onSaved();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Spremanje nije uspjelo.', 'Save failed.'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div>
            <button type="button" className="secondary compact" onClick={onClose} style={{ marginBottom: 8 }}>← {t('Natrag na popis', 'Back to list')}</button>
            <h3 style={{ margin: '0 0 0.6rem' }}>
                {id === 'new' ? t('Novi nacrt', 'New draft') : `${t('Nacrt', 'Draft')} #${id}`}
                {id !== 'new' ? <span style={{ marginLeft: 8, fontSize: '0.75rem', fontWeight: 700, color: STATUS_COLOR[status] }}>{STATUS_LABEL[status][locale === 'en' ? 1 : 0]}</span> : null}
            </h3>

            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : (
                <>
                    {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : null}

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: '0.8rem', alignItems: 'flex-end' }}>
                        <label>{t('Datum', 'Date')}<br /><input type="date" value={headDatum} disabled={!canEditHeader} onChange={(e) => setHeadDatum(e.target.value)} /></label>
                        <label>{t('Vrsta', 'Type')}<br /><input value={headVrsta} disabled={!canEditHeader} placeholder="306" style={{ width: 90 }} onChange={(e) => setHeadVrsta(e.target.value)} /></label>
                        <label style={{ flex: '1 1 260px' }}>{t('Opis', 'Description')} *<br /><input value={headOpis} disabled={!canEditHeader} required style={{ width: '100%' }} onChange={(e) => setHeadOpis(e.target.value)} /></label>
                        <label style={{ flex: '1 1 260px' }}>{t('Napomena', 'Note')}<br /><input value={headNapomena} disabled={!canEditHeader} style={{ width: '100%' }} onChange={(e) => setHeadNapomena(e.target.value)} /></label>
                    </div>

                    {truncatedTotal !== null ? (
                        <p className="muted">{t(`Prikazano 500 od ${truncatedTotal}; uredi u XLS-u i ponovno uvezi.`, `Showing 500 of ${truncatedTotal}; edit in XLSX and re-import.`)}</p>
                    ) : null}

                    <datalist id={KONTO_LIST_ID}>{kontoOptions.map((k) => <option key={k.konto} value={k.konto}>{k.naziv ?? ''}</option>)}</datalist>

                    <div style={{ overflow: 'auto', maxHeight: 480 }}>
                        <table className="data-table compact">
                            <thead><tr>
                                <th>{t('Konto', 'Account')}</th><th>{t('Partner', 'Partner')}</th><th>MTP</th><th>{t('Dokument', 'Document')}</th>
                                <th>{t('Opis', 'Description')}</th><th>{t('Datum knjiženja', 'Posting date')}</th>
                                <th style={num}>{t('Duguje', 'Debit')}</th><th style={num}>{t('Potražuje', 'Credit')}</th><th></th>
                            </tr></thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r._key}>
                                        <td>
                                            <input list={KONTO_LIST_ID} value={r.konto} disabled={!canEditStavke} style={{ width: 80 }}
                                                onChange={(e) => { const v = e.target.value; updateRow(r._key, 'konto', v); if (v.trim().length >= 2) fetchKonta(v.trim()); }} />
                                            {kontoCache[r.konto] ? <div className="muted" style={{ fontSize: '0.72rem' }}>{kontoCache[r.konto]}</div> : null}
                                        </td>
                                        <td><input value={r.partner} disabled={!canEditStavke} style={{ width: 120 }} onChange={(e) => updateRow(r._key, 'partner', e.target.value)} /></td>
                                        <td><input value={r.mtp} disabled={!canEditStavke} style={{ width: 90 }} onChange={(e) => updateRow(r._key, 'mtp', e.target.value)} /></td>
                                        <td><input value={r.dokument} disabled={!canEditStavke} style={{ width: 100 }} onChange={(e) => updateRow(r._key, 'dokument', e.target.value)} /></td>
                                        <td><input value={r.opis} disabled={!canEditStavke} style={{ width: 160 }} onChange={(e) => updateRow(r._key, 'opis', e.target.value)} /></td>
                                        <td><input type="date" value={r.datum_knjizenja} disabled={!canEditStavke} onChange={(e) => updateRow(r._key, 'datum_knjizenja', e.target.value)} /></td>
                                        <td><input type="text" inputMode="decimal" value={r.duguje} disabled={!canEditStavke} style={{ width: 90, textAlign: 'right' }}
                                            onChange={(e) => updateRow(r._key, 'duguje', e.target.value)} onBlur={(e) => updateRow(r._key, 'duguje', formatMoneyInput(e.target.value, locale))} /></td>
                                        <td><input type="text" inputMode="decimal" value={r.potrazuje} disabled={!canEditStavke} style={{ width: 90, textAlign: 'right' }}
                                            onChange={(e) => updateRow(r._key, 'potrazuje', e.target.value)} onBlur={(e) => updateRow(r._key, 'potrazuje', formatMoneyInput(e.target.value, locale))} /></td>
                                        <td>{canEditStavke ? <button type="button" className="secondary compact" onClick={() => removeRow(r._key)}>✕</button> : null}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ fontWeight: 700 }}>
                                    <td colSpan={6}>{t('Ukupno', 'Total')}</td>
                                    <td style={num}>{formatMoney(sums.d, locale)}</td><td style={num}>{formatMoney(sums.p, locale)}</td><td></td>
                                </tr>
                                <tr>
                                    <td colSpan={6}>{t('Razlika D - P', 'Debit - credit')}</td>
                                    <td colSpan={2} style={{ ...num, fontWeight: 700, color: Math.abs(razlika) >= 0.005 ? '#b91c1c' : undefined }}>{formatMoney(razlika, locale)}</td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    {canEditStavke ? <button type="button" className="secondary compact" style={{ marginTop: 6 }} onClick={addRow}>{t('Dodaj redak', 'Add row')}</button> : null}

                    {canEditHeader ? (
                        <div style={{ marginTop: '1rem' }}>
                            <button type="button" className="compact" disabled={saving} onClick={save}>{saving ? t('Spremam…', 'Saving…') : t('Spremi', 'Save')}</button>
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}

function NacrtiSection({ locale, token, company, godina }: { locale: Locale; token: string | null; company: string; godina: number }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [list, setList] = useState<NacrtiResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editorId, setEditorId] = useState<number | 'new' | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [radna, setRadna] = useState<Radna>('zajednicka');

    const [importOpen, setImportOpen] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importDatum, setImportDatum] = useState(new Date().toISOString().slice(0, 10));
    const [importVrsta, setImportVrsta] = useState('');
    const [importOpis, setImportOpis] = useState('');
    const [importIzvorOpis, setImportIzvorOpis] = useState('');
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState<string | null>(null);
    const [importError, setImportError] = useState<string | null>(null);

    const load = () => {
        setLoading(true);
        setError(null);
        api<NacrtiResp>(`/gl-analitika/nacrti?${qs({ company, godina, radna })}`, token)
            .then(setList)
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')))
            .finally(() => setLoading(false));
    };

    useEffect(load, [company, godina, token, radna]); // eslint-disable-line react-hooks/exhaustive-deps

    async function changeStatus(n: NacrtHead, newStatus: NacrtStatus) {
        let faros_broj: string | undefined;
        if (newStatus === 'proknjizeno') {
            const unos = window.prompt(t('Broj temeljnice u Farosu (opcionalno):', 'Faros journal number (optional):'), n.faros_broj ?? '');
            if (unos === null) return;
            faros_broj = unos.trim() || undefined;
        }
        setBusyId(n.id);
        setError(null);
        try {
            await mutate(`/gl-analitika/nacrti/${n.id}/status?${qs({ company })}`, token, 'POST', { status: newStatus, ...(faros_broj ? { faros_broj } : {}) });
            load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Promjena statusa nije uspjela.', 'Status change failed.'));
        } finally {
            setBusyId(null);
        }
    }

    async function premjesti(n: NacrtHead) {
        const cilj: Radna = n.radna === 'moja' ? 'zajednicka' : 'moja';
        setBusyId(n.id);
        setError(null);
        try {
            await mutate(`/gl-analitika/nacrti/${n.id}?${qs({ company })}`, token, 'PUT', { radna: cilj });
            load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Premještanje nije uspjelo.', 'Move failed.'));
        } finally {
            setBusyId(null);
        }
    }

    async function remove(n: NacrtHead) {
        if (!window.confirm(t(`Obrisati nacrt #${n.id} (${n.opis})?`, `Delete draft #${n.id} (${n.opis})?`))) return;
        setBusyId(n.id);
        setError(null);
        try {
            await mutate(`/gl-analitika/nacrti/${n.id}?${qs({ company })}`, token, 'DELETE');
            load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Brisanje nije uspjelo.', 'Delete failed.'));
        } finally {
            setBusyId(null);
        }
    }

    async function doImport(e: React.FormEvent) {
        e.preventDefault();
        if (!importFile) return;
        setImporting(true);
        setImportError(null);
        setImportResult(null);
        try {
            const fd = new FormData();
            fd.append('file', importFile);
            fd.append('godina', String(godina));
            fd.append('radna', radna);
            if (importDatum) fd.append('datum', importDatum);
            if (importVrsta) fd.append('vrsta', importVrsta);
            if (importOpis) fd.append('opis', importOpis);
            if (importIzvorOpis) fd.append('izvor_opis', importIzvorOpis);
            const res = await uploadFile<{ nacrti: Array<{ id: number; list: string; stavki: number }> }>(`/gl-analitika/nacrti/import?${qs({ company })}`, token, fd);
            const summary = res.nacrti.map((n) => `${n.list} (${formatNumber(n.stavki, locale)} ${t('stavki', 'lines')})`).join(', ');
            setImportResult(`${t('Uvezeno', 'Imported')}: ${summary}`);
            setImportFile(null);
            load();
        } catch (e) {
            setImportError(e instanceof Error ? e.message : t('Uvoz nije uspio.', 'Import failed.'));
        } finally {
            setImporting(false);
        }
    }

    if (editorId !== null) {
        return (
            <NacrtEditor
                locale={locale} token={token} company={company} godina={godina} id={editorId} radna={radna}
                onClose={() => setEditorId(null)}
                onSaved={() => { setEditorId(null); load(); }}
            />
        );
    }

    const m = (v: number) => formatMoney(v, locale);

    return (
        <div>
            <p className="muted">{t('Nacrt koji se pojavi u sljedećem uvozu glavne knjige (ista konta i iznosi) sam prelazi u Proknjiženo.', 'A draft that appears in the next general ledger import (same accounts and amounts) automatically moves to Posted.')}</p>

            <div style={{ display: 'flex', gap: 4, marginBottom: '0.6rem' }}>
                <button type="button" className={radna === 'zajednicka' ? 'compact' : 'secondary compact'} onClick={() => setRadna('zajednicka')}>{t('Zajednička radna bilanca', 'Shared working balance')}</button>
                <button type="button" className={radna === 'moja' ? 'compact' : 'secondary compact'} onClick={() => setRadna('moja')}>{t('Moja radna bilanca', 'My working balance')}</button>
            </div>
            <p className="muted" style={{ marginTop: 0 }}>{radna === 'moja'
                ? t('Nacrti u tvojoj radnoj bilanci vidljivi su samo tebi; služe za probu prije nego ih premjestiš u zajedničku.', 'Drafts in your working balance are visible only to you; use them to test before moving them to the shared one.')
                : t('Nacrti u zajedničkoj radnoj bilanci vidljivi su svima s pravom na modul 10.1.', 'Drafts in the shared working balance are visible to everyone with access to module 10.1.')}</p>

            <div style={{ display: 'flex', gap: 8, marginBottom: '0.8rem', flexWrap: 'wrap' }}>
                <button type="button" className="compact" onClick={() => setEditorId('new')}>{t('Novi nacrt', 'New draft')}</button>
                <button type="button" className="secondary compact" onClick={() => setImportOpen((o) => !o)}>{t('Uvoz iz XLSX', 'Import from XLSX')}</button>
                {loading ? <span className="muted">{t('Učitavam…', 'Loading…')}</span> : null}
            </div>

            {importOpen ? (
                <form onSubmit={doImport} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.8rem', padding: '0.6rem', border: '1px solid #ddd', borderRadius: 6 }}>
                    <label>{t('Datoteka (.xlsx)', 'File (.xlsx)')}<br />
                        <input type="file" accept=".xlsx" onChange={(e) => setImportFile(e.target.files?.[0] ?? null)} />
                    </label>
                    <label>{t('Datum', 'Date')}<br /><input type="date" value={importDatum} onChange={(e) => setImportDatum(e.target.value)} /></label>
                    <label>{t('Vrsta', 'Type')}<br /><input value={importVrsta} onChange={(e) => setImportVrsta(e.target.value)} placeholder="306" style={{ width: 90 }} /></label>
                    <label>{t('Opis (prefiks)', 'Description (prefix)')}<br /><input value={importOpis} onChange={(e) => setImportOpis(e.target.value)} style={{ width: 180 }} /></label>
                    <label>{t('Izvor', 'Source')}<br /><input value={importIzvorOpis} onChange={(e) => setImportIzvorOpis(e.target.value)} placeholder="DMS 715 v4" style={{ width: 150 }} /></label>
                    <button type="submit" className="compact" disabled={importing || !importFile}>{importing ? t('Uvozim…', 'Importing…') : t('Uvezi', 'Import')}</button>
                    <p className="muted" style={{ flexBasis: '100%', margin: '4px 0 0' }}>{t('Svaki list s kolonama Konto, Duguje, Potražuje postaje jedan nacrt (npr. T1-T5).', 'Each sheet with Konto, Duguje, Potražuje columns becomes one draft (e.g. T1-T5).')}</p>
                    {importResult ? <p style={{ flexBasis: '100%', margin: '4px 0 0' }}>{importResult}</p> : null}
                    {importError ? <p style={{ flexBasis: '100%', margin: '4px 0 0', color: '#b91c1c' }}>{importError}</p> : null}
                </form>
            ) : null}

            {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : null}

            {list ? (
                <div style={{ overflow: 'auto' }}>
                    <table className="data-table compact">
                        <thead><tr>
                            <th>ID</th><th>{t('Datum', 'Date')}</th><th>{t('Opis', 'Description')}</th><th>{t('Izvor', 'Source')}</th>
                            <th style={num}>{t('Stavki', 'Lines')}</th><th style={num}>{t('Duguje', 'Debit')}</th><th style={num}>{t('Potražuje', 'Credit')}</th>
                            <th>D=P</th><th>{t('Status', 'Status')}</th><th>Faros</th><th>{t('Radnje', 'Actions')}</th>
                        </tr></thead>
                        <tbody>
                            {list.nacrti.map((n) => {
                                const transitions = list.prijelazi[n.status] ?? [];
                                const canDelete = n.status === 'nacrt' || n.status === 'odbaceno';
                                return (
                                    <tr key={n.id}>
                                        <td>#{n.id}</td>
                                        <td>{n.datum ? formatDate(n.datum, locale) : ''}</td>
                                        <td>{n.opis}</td>
                                        <td title={n.izvor_opis ?? ''}>{n.izvor ?? ''}</td>
                                        <td style={num}>{formatNumber(n.stavki, locale)}</td>
                                        <td style={num}>{m(n.duguje)}</td>
                                        <td style={num}>{m(n.potrazuje)}</td>
                                        <td>{n.uravnotezen ? t('da', 'yes') : <strong style={{ color: '#b91c1c' }}>{t('ne', 'no')}</strong>}</td>
                                        <td><span style={{ fontSize: '0.75rem', fontWeight: 700, color: STATUS_COLOR[n.status] }}>{STATUS_LABEL[n.status][locale === 'en' ? 1 : 0]}</span></td>
                                        <td>{n.faros_vrsta || n.faros_broj ? `${n.faros_vrsta ?? ''} ${n.faros_broj ?? ''}`.trim() : ''}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                <button type="button" className="secondary compact" onClick={() => setEditorId(n.id)}>{t('Otvori', 'Open')}</button>
                                                <button type="button" className="secondary compact" disabled={busyId === n.id}
                                                    onClick={() => downloadFile(`/gl-analitika/nacrti/${n.id}/export.xlsx`, token, `nacrt-${n.id}.xlsx`).catch((e) => setError(e instanceof Error ? e.message : t('Izvoz nije uspio.', 'Export failed.')))}>
                                                    {t('XLS', 'XLS')}
                                                </button>
                                                {transitions.map((s) => (
                                                    <button key={s} type="button" className="secondary compact" disabled={busyId === n.id} onClick={() => changeStatus(n, s)}>
                                                        {STATUS_LABEL[s][locale === 'en' ? 1 : 0]}
                                                    </button>
                                                ))}
                                                {n.status === 'nacrt' || n.status === 'odobreno' ? (
                                                    <button type="button" className="secondary compact" disabled={busyId === n.id} onClick={() => premjesti(n)}>
                                                        {n.radna === 'moja' ? t('U zajedničku', 'To shared') : t('U moju', 'To mine')}
                                                    </button>
                                                ) : null}
                                                {canDelete ? (
                                                    <button type="button" className="secondary compact" disabled={busyId === n.id} onClick={() => remove(n)} style={{ color: '#b91c1c' }}>
                                                        {t('Obriši', 'Delete')}
                                                    </button>
                                                ) : null}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {list.nacrti.length === 0 ? <p className="muted">{t('Nema nacrta za odabranu firmu i godinu.', 'No drafts for the selected company and year.')}</p> : null}
                </div>
            ) : null}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/**
 * Modul 10.1, tab Temeljnice i nacrti. Arhiva drzi pregled stvarno
 * proknjizenih temeljnica iz ANAL_GK uvoza (uravnotezenost, zadnja promjena);
 * Nacrti drze interne ispravke prije knjizenja u Farosu - rucno ili uvezene
 * iz XLSX (npr. DMS dopis), s tijekom nacrt -> odobreno -> proknjizeno.
 */
export function GlTemeljniceTab({ locale, token, company, godina }: { locale: Locale; token: string | null; company: string; godina: number }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [section, setSection] = useState<Section>('arhiva');

    return (
        <div>
            <div style={{ display: 'flex', gap: 4, marginBottom: '0.8rem' }}>
                <button type="button" className={section === 'arhiva' ? 'compact' : 'secondary compact'} onClick={() => setSection('arhiva')}>{t('Arhiva temeljnica', 'Journal archive')}</button>
                <button type="button" className={section === 'nacrti' ? 'compact' : 'secondary compact'} onClick={() => setSection('nacrti')}>{t('Nacrti temeljnica', 'Journal drafts')}</button>
            </div>
            {section === 'arhiva'
                ? <ArhivaSection locale={locale} token={token} company={company} godina={godina} />
                : <NacrtiSection locale={locale} token={token} company={company} godina={godina} />}
        </div>
    );
}
